import { useAppSelector } from '@/store';
import { gameSelector } from '@/store/reducers/game';
import { NewGameTypes } from '@/types/game';
import classes from 'classnames';
import SVG from 'react-inlinesvg';

import { MultiChoiceOptionStatusProps } from './types';

import correct from '@/assets/svg/correct-circle.svg';
import wrong from '@/assets/svg/wrong-circle.svg';

import classNames from '../MultiChoiceOptions.module.scss';
import { unitSelector } from '@/store/reducers/unit';

const Alphabet = ({ alphabet = '', className }: { alphabet?: string; className?: string }) => {
  const _alphabet = alphabet.toLowerCase().trim();

  if (_alphabet === 'a') {
    return (
      <svg className={className} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none'>
        <rect
          x='0.666667'
          y='0.666667'
          width='30.6667'
          height='30.6667'
          rx='15.3333'
          style={{
            stroke: 'currentcolor',
            strokeWidth: '1.33333',
          }}
        />
        <path
          d='M19.36 23L18.44 20.34H12.88L11.96 23H9.02L14.04 9.02H17.3L22.32 23H19.36ZM17.68 18.1L15.66 12.26L13.64 18.1H17.68Z'
          style={{
            fill: 'currentColor',
          }}
        />
      </svg>
    );
  }

  if (_alphabet === 'b') {
    return (
      <svg className={className} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none'>
        <rect
          x='0.666667'
          y='0.666667'
          width='30.6667'
          height='30.6667'
          rx='15.3333'
          style={{
            stroke: 'currentcolor',
            strokeWidth: '1.33333',
          }}
        />
        <path
          d='M18.8084 18.88C18.8084 17.68 17.9484 16.96 16.5884 16.96H13.8284V20.72H16.6484C18.0084 20.72 18.8084 20.06 18.8084 18.88ZM18.4684 13.06C18.4684 11.94 17.7084 11.3 16.3884 11.3H13.8284V14.8H16.3884C17.7084 14.8 18.4684 14.2 18.4684 13.06ZM21.6484 19.22C21.6484 21.44 19.9484 23 17.0484 23H11.0284V9.04H16.7884C19.7084 9.04 21.3284 10.56 21.3284 12.64C21.3284 14.34 20.3084 15.4 18.9484 15.84C20.5284 16.12 21.6484 17.58 21.6484 19.22Z'
          style={{
            fill: 'currentColor',
          }}
        />
      </svg>
    );
  }

  if (_alphabet === 'c') {
    return (
      <svg className={className} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none'>
        <rect
          x='0.666667'
          y='0.666667'
          width='30.6667'
          height='30.6667'
          rx='15.3333'
          style={{
            stroke: 'currentcolor',
            strokeWidth: '1.33333',
          }}
        />
        <path
          d='M8.7 16C8.7 11.82 11.76 8.86 15.78 8.86C18.88 8.86 21.4 10.52 22.34 13.38H19.12C18.46 12.04 17.26 11.38 15.76 11.38C13.32 11.38 11.58 13.16 11.58 16C11.58 18.82 13.32 20.62 15.76 20.62C17.26 20.62 18.46 19.96 19.12 18.6H22.34C21.4 21.48 18.88 23.12 15.78 23.12C11.76 23.12 8.7 20.18 8.7 16Z'
          style={{
            fill: 'currentColor',
          }}
        />
      </svg>
    );
  }

  if (_alphabet === 'd') {
    return (
      <svg className={className} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none'>
        <rect
          x='0.666667'
          y='0.666667'
          width='30.6667'
          height='30.6667'
          rx='15.3333'
          style={{
            stroke: 'currentcolor',
            strokeWidth: '1.33333',
          }}
        />
        <path
          d='M15.4241 9.04C19.8841 9.04 22.7841 11.8 22.7841 16.04C22.7841 20.26 19.8841 23 15.4241 23H10.5441V9.04H15.4241ZM13.3441 20.62H15.3241C18.2641 20.62 19.9241 18.94 19.9241 16.04C19.9241 13.14 18.2641 11.4 15.3241 11.4H13.3441V20.62Z'
          style={{
            fill: 'currentColor',
          }}
        />
      </svg>
    );
  }

  return <span className={className}>{alphabet}</span>;
};

const MultiChoiceOptionStatus = (props: MultiChoiceOptionStatusProps) => {
  const { isOptionSelected, isOptionCorrect, isDictionary, alphabet } = props;
  const { gameQuestions, isLevelTest } = useAppSelector(gameSelector);
  const { selectedUnit } = useAppSelector(unitSelector);
  //FIXME: NEED TO REPLACE WITH CORRECT GAMETYPE
  const TEMP_GAMETYPE = (gameQuestions[0]?.gameType as any) || 8;

  const selectedGameType = isLevelTest ? 'leveltest' : selectedUnit?.order || 1;

  //FIXME: This area is for new game types with new API. Need to make in correct way
  if (TEMP_GAMETYPE === NewGameTypes.AudioToImage || TEMP_GAMETYPE === NewGameTypes.TextToImage) {
    return (
      <>
        {!isOptionSelected ? (
          isOptionCorrect && <SVG src={correct} className={classNames.answerStatusImg} />
        ) : (
          <div className={classes(classNames.answerStatus, classNames.answerStatusImgWrapper)}>
            <SVG src={isOptionCorrect ? correct : wrong} className={classNames.answerStatusImg} />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {!isOptionSelected ? (
        <Alphabet
          className={classes(classNames.alphabet, {
            [classNames[`alphabet-${selectedGameType}`]]: !isDictionary,
            [classNames.alphabetAnswer]: isOptionCorrect,
          })}
          alphabet={alphabet}
        />
      ) : (
        <div className={classNames.answerStatus}>
          <SVG src={isOptionCorrect ? correct : wrong} />
        </div>
      )}
    </>
  );
};

export default MultiChoiceOptionStatus;
